import { useParams, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/data/translations";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const serviceImages = {
  "business-car": "https://images.unsplash.com/photo-1652453822974-c7e827141366?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxibGFjayUyME1lcmNlZGVzJTIwQmVueiUyMGNsb3NlJTIwdXAlMjBkZXRhaWwlMjBoZWFkbGlnaHQlMjBncmlsbGV8ZW58MHx8fHwxNzc1Mjk4NjAxfDA&ixlib=rb-4.1.0&q=85",
  minibus: "https://images.unsplash.com/photo-1652453822974-c7e827141366?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxibGFjayUyME1lcmNlZGVzJTIwQmVueiUyMGNsb3NlJTIwdXAlMjBkZXRhaWwlMjBoZWFkbGlnaHQlMjBncmlsbGV8ZW58MHx8fHwxNzc1Mjk4NjAxfDA&ixlib=rb-4.1.0&q=85",
  cargo: "https://images.unsplash.com/photo-1658328565756-f42c28c010c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxibGFjayUyMGRlbGl2ZXJ5JTIwdmFuJTIwaGlnaHdheXxlbnwwfHx8fDE3NzUyOTc0MTF8MA&ixlib=rb-4.1.0&q=85",
  pallet: "https://images.unsplash.com/photo-1658328565756-f42c28c010c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxibGFjayUyMGRlbGl2ZXJ5JTIwdmFuJTIwaGlnaHdheXxlbnwwfHx8fDE3NzUyOTc0MTF8MA&ixlib=rb-4.1.0&q=85",
};

const serviceOrder = ["business-car", "minibus", "cargo", "pallet"];

export default function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const data = t.servicePages[lang]?.[slug];

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <h1 className="font-heading text-4xl font-bold text-[#0A0B10] mb-4">404</h1>
        <Link to="/" className="text-[#FF3B30] underline">
          {lang === "cs" ? "Zpet na hlavni stranku" : "Back to homepage"}
        </Link>
      </div>
    );
  }

  const currentIdx = serviceOrder.indexOf(slug);
  const prevSlug = currentIdx > 0 ? serviceOrder[currentIdx - 1] : null;
  const nextSlug = currentIdx < serviceOrder.length - 1 ? serviceOrder[currentIdx + 1] : null;

  return (
    <div>
      {/* Hero */}
      <section data-testid={`service-hero-${slug}`} className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-14 sm:pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Link
                to="/"
                data-testid="service-back-link"
                className="inline-flex items-center gap-2 text-sm text-[#666666] hover:text-[#0A0B10] mb-6 sm:mb-8 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {lang === "cs" ? "Vse sluzby" : "All services"}
              </Link>
              <h1
                data-testid="service-title"
                className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-[#0A0B10] mb-4 sm:mb-6"
              >
                {data.title}
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-[#666666] leading-relaxed mb-6 sm:mb-8 max-w-lg" data-testid="service-desc">
                {data.heroDesc}
              </p>
              <a href="https://wa.me/420607179165" target="_blank" rel="noreferrer" data-testid="service-whatsapp-cta">
                <Button className="rounded-none bg-[#FF3B30] text-white hover:bg-[#0A0B10] text-sm font-medium px-8 h-11 sm:h-12 w-full sm:w-auto">
                  {data.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="overflow-hidden"
            >
              <img
                src={serviceImages[slug]}
                alt={data.title}
                className="w-full h-[220px] sm:h-[300px] md:h-[420px] object-cover"
                data-testid="service-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Specs & Routes */}
      <section className="bg-[#F5F5F5] border-y border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16">
            {/* Specs */}
            <div data-testid="service-specs">
              <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-medium tracking-tight text-[#0A0B10] mb-6 sm:mb-8">
                {lang === "cs" ? "Specifikace" : "Specifications"}
              </h2>
              <ul className="space-y-4">
                {data.specs.map((spec, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                    <span className="text-[#0A0B10] text-sm leading-relaxed">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Routes */}
            <div data-testid="service-routes">
              <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-medium tracking-tight text-[#0A0B10] mb-6 sm:mb-8">
                {lang === "cs" ? "Trasy a ceny" : "Routes & Prices"}
              </h2>
              <div className="border-t-2 border-[#0A0B10]">
                <div className="flex justify-between py-4 border-b border-[#0A0B10] text-xs uppercase tracking-[0.2em] font-semibold text-[#0A0B10]">
                  <span>{lang === "cs" ? "Trasa" : "Route"}</span>
                  <span>{lang === "cs" ? "Cena" : "Price"}</span>
                </div>
                {data.routes.map((r, i) => (
                  <div key={i} className="flex justify-between items-baseline gap-4 py-4 sm:py-5 border-b border-[#E5E5E5]">
                    <span className="text-xs sm:text-sm text-[#0A0B10] min-w-0">{r.route}</span>
                    <span className="text-xs sm:text-sm font-semibold text-[#FF3B30] whitespace-nowrap flex-shrink-0">{r.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nav between services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex justify-between items-center gap-4">
          {prevSlug ? (
            <Link
              to={`/services/${prevSlug}`}
              data-testid="service-prev"
              className="flex items-center gap-2 text-xs sm:text-sm text-[#666666] hover:text-[#0A0B10] transition-colors min-w-0"
            >
              <ArrowLeft className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{t.servicePages[lang][prevSlug]?.title}</span>
            </Link>
          ) : (
            <div />
          )}
          {nextSlug ? (
            <Link
              to={`/services/${nextSlug}`}
              data-testid="service-next"
              className="flex items-center gap-2 text-xs sm:text-sm text-[#666666] hover:text-[#0A0B10] transition-colors min-w-0"
            >
              <span className="truncate">{t.servicePages[lang][nextSlug]?.title}</span>
              <ArrowRight className="w-4 h-4 flex-shrink-0" />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </section>
    </div>
  );
}
